import React, {useCallback, useEffect, useMemo, useState} from 'react';
import ScreenWrapper from "@hoc/ScreenWrapper";
import {NativeStackScreenProps} from "@react-navigation/native-stack";
import {PrivateStackParamList} from "@routes/types";
import CommonHeader from "@sharedComponents/Header/Header";
import {Theme, ThemedText, ThemedView} from "@app/theme";
import FetchRupeeIcon from "@app/assets/svg/FetchRupeeIcon";
import RefreshIcon from "@app/assets/svg/RefreshIcon";
import Button from "@sharedComponents/Button/Button";
import {useTheme} from "@shopify/restyle";
import RightBlackArrow from "@app/assets/svg/RightBlackArrow";
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {TouchableOpacity} from "react-native";
import {pledgeLimitAPICall} from "@screens/MFCentral/MFCentralEvaluateFunds/services/mfCentralEvaluateFunds";
import {addCommasToNumber, getDateMonthYearFromEpochFromMicroseconds, trackEvent} from "@app/utils";
import PageLoader from "@sharedComponents/PageLoader/PageLoader";
import { EnhanceLimitCard } from '@screens/MFCentral/MFCentralAnchor/components/EnhanceLimitCard';
import { useIsEnhanceLimit } from '@hooks/useIsEnhanceLimit';
import { AvailableCASMap, setAuthCAS, setAvailableCAS, setMinimumEligibleLimit } from '@store/slices/applicationSlice';
import { useFocusEffect } from '@react-navigation/native';
import { AssetRepository } from '@app/screens/FetchPortfolio/type';
import { usePledgeData } from '@app/hooks/usePledgeData';
import { updatePartnerParams } from '@app/store/slices/platformSlice';

type PledgeLimitResponse = {
    stepResponseObject?: {
        availableCreditAmount?: number;
        minimumEligibleLimit?: { amount?: number };
        totalPortfolioAmount?: number;
        repositoryAssetMetadataMap?: {
            MFCENTRAL?: { casFetchDate?: string };
        };
    };
};

export type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'MFC_FETCH_ANCHOR'>;

const MFCentralAnchor = ({navigation, route}: ScreenProps) => {
    const {colors} = useTheme<Theme>();
    const {userDetails} = useAppSelector(state => state.auth);
    const [pledgeLimitResponse, setPledgeLimitResponse] = useState<PledgeLimitResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const { isFromEnhanceLimit } = useIsEnhanceLimit()
    const { pledgeData, loading: pledgeDataLoading } = usePledgeData();
    const dispatch = useAppDispatch()
    const {authCAS, availableCAS} = useAppSelector(state => state.application)

    const fetchPledgeLimit = useCallback(async () => {
        setLoading(true);
        try {
            const response = await pledgeLimitAPICall({
                applicationId: userDetails?.linkedApplications[0]?.applicationId
            });
            setPledgeLimitResponse(response?.data);
            dispatch(setAvailableCAS(response?.data?.stepResponseObject?.availableCAS))
            dispatch(setAuthCAS(response?.data?.stepResponseObject));
            dispatch(setMinimumEligibleLimit(response?.data?.stepResponseObject?.minimumEligibleLimit))
            trackEvent('MFC_FETCH_ANCHOR_PAGE_VIEWED_MAIN', {
                availableCreditAmount: response?.data?.stepResponseObject?.availableCreditAmount ?? 0,
                minimumEligibleLimit: response?.data?.stepResponseObject?.minimumEligibleLimit?.amount ?? 0
            })
            if((response?.data?.stepResponseObject?.availableCreditAmount ?? 0) >= (response?.data?.stepResponseObject?.minimumEligibleLimit?.amount ?? 0)){
                dispatch(updatePartnerParams({showCall:true}))
            }
        } finally {
            setLoading(false);
        }
    }, [userDetails , dispatch]);

    useFocusEffect(
        useCallback(() => {
            if (!pledgeLimitResponse) {
                fetchPledgeLimit();
            }
            return () => {};
        }, [fetchPledgeLimit, pledgeLimitResponse])
    );

    const {
        availableCreditAmount = 0,
        minimumEligibleLimit = {amount: 0},
        totalPortfolioAmount = 0,
        repositoryAssetMetadataMap = {},
    } = pledgeLimitResponse?.stepResponseObject || {};

    const lastFetchedDate = useMemo(() => {
        const dateStr = repositoryAssetMetadataMap?.MFCENTRAL?.casFetchDate;
        if (!dateStr) return '';
        return getDateMonthYearFromEpochFromMicroseconds(dateStr)
    }, [repositoryAssetMetadataMap]);

    const isEligible = availableCreditAmount >= (minimumEligibleLimit?.amount || 0);

    const renderFundsCard = () => (
        <ThemedView borderColor={"border-secondary"} borderRadius={"lg"} padding={"lg"} alignSelf={"center"}
                    borderWidth={1} alignItems={"center"} width={"100%"}>
            <FetchRupeeIcon/>
            {isFromEnhanceLimit ? (
                <ThemedText variant={"h5s"} color={"text-primary"} mt={"lg"}>
                    {"Additional credit limit"}
                </ThemedText>
            ) : (
                <ThemedText variant={"h5s"} color={"text-primary"} mt={"lg"}>
                    {isEligible ? "Your eligible credit limit" : "Your credit limit is low"}
                </ThemedText>
            )}
            <ThemedView flexDirection={"row"} alignItems={"center"} mt={"sm"}>
                <ThemedText variant={"l1"} color={isEligible ? "text-emphasis-secondary" : "text-primary"}>
                    ₹
                </ThemedText>
                <ThemedText variant={"h1b"} color={isEligible ? "text-emphasis-secondary" : "text-primary"}>
                    {addCommasToNumber(availableCreditAmount)}
                </ThemedText>
            </ThemedView>

            {!isEligible && (
                <ThemedView width={"100%"} bg={"bg-fill-error"} borderRadius={"sm"} padding={"md"} mt={"lg"}>
                    <ThemedText variant={"b4r"} textAlign={"center"}>
                        A minimum credit limit of{' '}
                        <ThemedText variant={"b4s"} color={"text-error"} mr={"xs"} ml={"xs"}>
                            ₹{(addCommasToNumber(minimumEligibleLimit.amount) || 0)}
                        </ThemedText>
                        {' '}is required to apply for a loan
                    </ThemedText>
                </ThemedView>
            )}
        </ThemedView>
    );

    return (
        <ScreenWrapper screenName={route?.name} additionalProperties={{newArchRoute: true}}>
            {loading ? <PageLoader/>
                :
                <>
                    <CommonHeader label='' navigation={navigation} pageName={route.name}/>

                    <ThemedView backgroundColor="bg-surface-primary" flex={1} padding={'lg'}>
                        {isFromEnhanceLimit && <EnhanceLimitCard creditLimit={pledgeData?.stepResponseObject?.existingSanctionedLimit} />}
                        {renderFundsCard()}
                        <ThemedView flexDirection={'row'} alignSelf={'center'} mt={'sm'} mb={'2xl'}>
                            <ThemedText variant={'b4r'}>Last fetched on {lastFetchedDate || 'N/A'}</ThemedText>
                            {isEligible &&
                                <TouchableOpacity activeOpacity={1} onPress={() => {
                                    navigation.navigate('MF_FETCH_PORTFOLIO');
                                    trackEvent('MFC_FETCH_ANCHOR_REFRESH_PORTFOLIO_CLICKED')
                                }}>
                                    <ThemedView flexDirection={'row'} ml={'sm'} justifyContent={'center'}
                                                alignItems={'center'}>
                                        <ThemedText variant={'b4s'} mr={'xs'} color={'text-action-primary'}>
                                            Refresh
                                        </ThemedText>
                                        <RefreshIcon color={colors['icon-action-primary']} />
                                    </ThemedView>
                                </TouchableOpacity>
                            }
                        </ThemedView>
                        <Button
                            eventName={isEligible ? 'MFC_FETCH_ANCHOR_SET_CREDIT_LIMIT_CLICKED' : 'MFC_FETCH_ANCHOR_REFRESH_PORTFOLIO_CLICKED'}
                            label={isEligible ? 'Set credit limit' : 'Refresh portfolio'}
                            type="SOLID"
                            size="MD"
                            disabled={false}
                            handlePress={() => {
                                navigation.navigate(isEligible ? 'SET_CREDIT_LIMIT': "MF_FETCH_PORTFOLIO");
                            }}
                            bg={colors['bg-action-primary']}
                            textColor={colors['text-primary']}
                        />
                        <TouchableOpacity onPress={() => {
                            trackEvent('MFC_FETCH_ANCHOR_FETCHED_MUTUAL_FUNDS_CLICKED')
                            navigation.navigate(
                                'PORTFOLIO_FROM_RTA', 
                                {
                                    rta: AssetRepository.MFCENTRAL, 
                                    lastFetchedOn: repositoryAssetMetadataMap?.MFCENTRAL?.casFetchDate 
                                                  ? Number(repositoryAssetMetadataMap?.MFCENTRAL?.casFetchDate) 
                                                  : 0
                                });
                        }}>
                            <ThemedView mt={'4xl'} borderRadius={'lg'} borderWidth={1} borderColor={'border-primary'}
                                        bg={'bg-action-disabled'} paddingVertical={'md'} paddingLeft={'lg'}
                                        paddingRight={'2xl'}
                                        flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                <ThemedView>
                                    <ThemedText variant={'b4r'} color={'text-secondary'}>
                                        Fetched mutual funds
                                    </ThemedText>
                                    <ThemedText variant={'b2s'} color={'text-primary'} mt={'xs'}>
                                        ₹{addCommasToNumber(totalPortfolioAmount)}
                                    </ThemedText>
                                </ThemedView>
                                <RightBlackArrow />
                            </ThemedView>
                        </TouchableOpacity>
                    </ThemedView>
                </>
            }
        </ScreenWrapper>
    );
};

export default MFCentralAnchor;
